/**
 * AvailabilityCalendar Component
 *
 * Month calendar view showing availability blocks with color coding.
 * Click day to add, click block to delete. Includes legend.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { AvailabilityBlock, AvailabilityBlockType, CreateAvailabilityRequest } from '@/types/contractors';
import { availabilityApi } from '@/services/contractorsApi';

interface AvailabilityCalendarProps {
  isAdmin?: boolean;
  contractorUserId?: number;
}

const AVAILABILITY_COLORS: Record<AvailabilityBlockType, { bg: string; text: string; label: string }> = {
  available: { bg: 'bg-green-200', text: 'text-green-800', label: 'Available' },
  unavailable: { bg: 'bg-red-200', text: 'text-red-800', label: 'Unavailable' },
  on_leave: { bg: 'bg-orange-200', text: 'text-orange-800', label: 'On Leave' },
  busy: { bg: 'bg-gray-300', text: 'text-gray-800', label: 'Busy' },
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Mon=0
}

function formatDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function AvailabilityCalendar({ isAdmin = false, contractorUserId }: AvailabilityCalendarProps): React.JSX.Element {
  const { t } = useTranslation('contractors');

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [blocks, setBlocks] = useState<AvailabilityBlock[]>([]);
  const [_isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form
  const [showForm, setShowForm] = useState(false);
  const [formDate, setFormDate] = useState('');
  const [formBlockType, setFormBlockType] = useState<AvailabilityBlockType>('available');
  const [formNotes, setFormNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const startDate = formatDate(year, month, 1);
  const endDate = formatDate(year, month, getDaysInMonth(year, month));

  const fetchBlocks = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof availabilityApi.list>[0] = {
        start_date: startDate,
        end_date: endDate,
      };
      if (contractorUserId) params.contractor_user_id = contractorUserId;
      const result = await availabilityApi.list(params);
      setBlocks(Array.isArray(result) ? result : []);
    } catch {
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, contractorUserId, t]);

  useEffect(() => {
    void fetchBlocks();
  }, [fetchBlocks]);

  const navigateMonth = (delta: number): void => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 0) { newMonth = 11; newYear--; }
    if (newMonth > 11) { newMonth = 0; newYear++; }
    setMonth(newMonth);
    setYear(newYear);
  };

  const getBlocksForDate = (dateStr: string): AvailabilityBlock[] => {
    return blocks.filter((b) => b.start_date <= dateStr && b.end_date >= dateStr);
  };

  const handleDayClick = (day: number): void => {
    if (isAdmin) return; // Admin view only
    const dateStr = formatDate(year, month, day);
    setFormDate(dateStr);
    setFormBlockType('available');
    setFormNotes('');
    setShowForm(true);
  };

  const handleAddBlock = async (): Promise<void> => {
    if (!formDate) return;
    setSaving(true);
    setError(null);
    try {
      const payload: CreateAvailabilityRequest = {
        block_type: formBlockType,
        start_date: formDate,
        end_date: formDate,
        notes: formNotes || undefined,
      };
      await availabilityApi.create(payload);
      setShowForm(false);
      await fetchBlocks();
    } catch {
      setError(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlock = async (blockId: string): Promise<void> => {
    if (isAdmin) return;
    try {
      await availabilityApi.delete(blockId);
      await fetchBlocks();
    } catch {
      setError(t('errors.deleteFailed'));
    }
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="space-y-4">
      {/* Header with month navigation */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{t('availability.title')}</h2>
        <div className="flex items-center gap-3">
          <button onClick={() => navigateMonth(-1)} className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-medium text-gray-900 min-w-[140px] text-center">
            {MONTH_NAMES[month]} {year}
          </span>
          <button onClick={() => navigateMonth(1)} className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {(Object.entries(AVAILABILITY_COLORS) as Array<[AvailabilityBlockType, typeof AVAILABILITY_COLORS[AvailabilityBlockType]]>).map(([type, cfg]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${cfg.bg}`} />
            <span className="text-xs text-gray-600">{t(`availability.types.${type}`)}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>
      )}

      {/* Calendar grid */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {weekDays.map((day) => (
            <div key={day} className="px-2 py-2 text-xs font-semibold text-gray-500 text-center">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar cells */}
        <div className="grid grid-cols-7">
          {/* Empty cells before first day */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} className="min-h-[80px] border-b border-r border-gray-100 bg-gray-50/50" />
          ))}

          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = formatDate(year, month, day);
            const dayBlocks = getBlocksForDate(dateStr);
            const isToday = dateStr === formatDate(now.getFullYear(), now.getMonth(), now.getDate());

            return (
              <div
                key={day}
                onClick={() => handleDayClick(day)}
                className={`min-h-[80px] border-b border-r border-gray-100 p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                  isToday ? 'bg-blue-50/50' : ''
                }`}
              >
                <div className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                  {day}
                </div>
                <div className="space-y-0.5">
                  {dayBlocks.map((block) => {
                    const cfg = AVAILABILITY_COLORS[block.block_type];
                    return (
                      <div
                        key={block.id}
                        onClick={(e) => { e.stopPropagation(); handleDeleteBlock(block.id); }}
                        className={`px-1 py-0.5 rounded text-xs truncate ${cfg.bg} ${cfg.text} ${!isAdmin ? 'cursor-pointer hover:opacity-70' : ''}`}
                        title={block.notes ?? block.block_type}
                      >
                        {block.block_type}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Remaining empty cells */}
          {Array.from({ length: (7 - ((firstDay + daysInMonth) % 7)) % 7 }).map((_, i) => (
            <div key={`trail-${i}`} className="min-h-[80px] border-b border-r border-gray-100 bg-gray-50/50" />
          ))}
        </div>
      </div>

      {/* Add block modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('availability.addBlock')}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="text-sm text-gray-600">{t('availability.date')}: <strong>{formDate}</strong></div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('availability.blockType')}</label>
                <select
                  value={formBlockType}
                  onChange={(e) => setFormBlockType(e.target.value as AvailabilityBlockType)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                >
                  <option value="available">{t('availability.types.available')}</option>
                  <option value="unavailable">{t('availability.types.unavailable')}</option>
                  <option value="on_leave">{t('availability.types.on_leave')}</option>
                  <option value="busy">{t('availability.types.busy')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('availability.notes')}</label>
                <input
                  type="text"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  placeholder={t('availability.notesPlaceholder')}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                {t('common.cancel')}
              </button>
              <button onClick={handleAddBlock} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
